<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { getOptimizedImageUrl } from '../utils/image'
import { useCartStore } from '../stores/cartStore'
import { useSettingsStore } from '../stores/settingsStore'
import { useWishlistStore } from '../stores/wishlistStore'
import { useCompareStore } from '../stores/compareStore'
import { useAuthStore } from '../stores/authStore'
import { useTrackingStore } from '../store/tracking'
import { useToast } from '../composables/useToast'
import FeatureIcon from '../components/ui/FeatureIcon.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const router = useRouter()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()
const wishlistStore = useWishlistStore()
const compareStore = useCompareStore()
const authStore = useAuthStore()
const trackingStore = useTrackingStore()
const { showToast } = useToast()

const addToCart = async (product) => {
  try {
    await cartStore.addToCart(product, 1);
    showToast(`เพิ่ม ${product.title || product.name} ลงในตะกร้าแล้ว`, 'success');
    trackingStore.trackEvent({
      type: 'add_to_cart',
      productId: product.id,
      productName: product.title || product.name,
      price: product.price,
      quantity: 1,
      source: 'product_card'
    });
  } catch (err) {
    showToast('ไม่สามารถเพิ่มลงตะกร้าได้', 'error');
  }
}

const toggleWishlistItem = async (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  if (!authStore.isAuthenticated) {
    showToast('กรุณาเข้าสู่ระบบก่อนเพิ่มรายการโปรด', 'warning');
    return;
  }
  const result = await wishlistStore.toggleWishlist(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการโปรดแล้ว' : 'นำออกจากรายการโปรดแล้ว', result.isAdded ? 'success' : 'info');
    trackingStore.trackEvent({
      type: result.isAdded ? 'add_to_wishlist' : 'remove_from_wishlist',
      productId: product.id,
      productName: product.title || product.name,
      source: 'product_card'
    });
  }
}

const toggleCompareItem = (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  const result = compareStore.toggleCompare(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการเปรียบเทียบแล้ว' : 'นำออกจากรายการเปรียบเทียบแล้ว', result.isAdded ? 'success' : 'info');
  } else if (result.error === 'max_reached') {
    showToast('เปรียบเทียบได้สูงสุด 4 ชิ้น', 'warning');
  }
}

const productSku = computed(() => {
  if (props.product?.sku) return props.product.sku
  if (props.product?.product_sku) return props.product.product_sku
  if (props.product?.model) return props.product.model
  
  const name = props.product?.title || props.product?.name || ''
  const match = name.match(/MS-[A-Z0-9-]+/i)
  if (match) return match[0]
  
  return null
})

const isOutOfStock = computed(() => {
  const val = props.product?.is_out_of_stock
  if (val === true || val === 1 || val === '1' || val === 'true') return true
  return false
})

const cardFeatures = computed(() => {
  const feat = props.product?.card_features
  if (!feat) return null
  if (typeof feat === 'string') {
    try { return JSON.parse(feat) } catch (e) { return null }
  }
  if (typeof feat === 'object') return feat
  return null
})

const formatPrice = (price) => {
  if (!price) return '0'
  return new Intl.NumberFormat('th-TH').format(price)
}



const now = ref(Date.now())
setInterval(() => { now.value = Date.now() }, 60000)

const getTimeRemaining = (endTime) => {
  if (!endTime) return null
  const total = Date.parse(endTime) - now.value
  if (total <= 0) return null
  const days = Math.floor(total / (1000 * 60 * 60 * 24))
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((total / 1000 / 60) % 60)
  
  if (days > 0) return `จบโปรใน ${days} วัน ${hours} ชม.`
  if (hours > 0) return `จบโปรใน ${hours} ชม. ${minutes} นาที`
  return `จบโปรใน ${minutes} นาที`
}
</script>

<template>
  <router-link 
    :to="'/products/' + (product.slug || product.id)" 
    class="group flex flex-col h-full rounded-[2rem] bg-white dark:bg-[#121826] border border-gray-100/80 dark:border-gray-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(16,185,129,0.12)] hover:border-emerald-200/50 transition-all duration-500 overflow-hidden relative"
  >
    <!-- Image Section (Top half) -->
    <div class="relative bg-gray-50 dark:bg-gray-800/50 aspect-square w-full flex items-center justify-center overflow-hidden">
      <img :src="getOptimizedImageUrl(product.image || product.image_url, 400)" :alt="product.title || product.name" width="600" height="600" class="w-full h-full object-cover object-center relative z-[1]">
      
      <!-- Background decoration for image -->
      <div class="absolute inset-0 bg-gradient-to-b from-black/5 to-transparent z-[2] opacity-0 group-hover:opacity-100 transition-opacity"></div>
      
      <!-- Badges (Top Left - 20% Compact Size) -->
      <div class="absolute top-3 left-3 z-[4] flex flex-col gap-1.5">
        <span v-if="product.isBestSeller || product.subtitle === 'ขายดี'" class="bg-[#e6f4ea] dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/80 text-[9.5px] font-extrabold px-2.5 py-1 rounded-full shadow-xs flex items-center gap-1">
          <span class="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
          ขายดี
        </span>
        <span v-else-if="product.subtitle === 'แนะนำ' || product.is_recommended" class="bg-orange-500 text-white text-[9.5px] font-extrabold px-2.5 py-1 rounded-full shadow-xs flex items-center gap-1">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
          แนะนำ
        </span>
        <span v-else-if="product.subtitle === 'ใหม่' || product.is_new" class="bg-blue-600 text-white text-[9.5px] font-extrabold px-2.5 py-1 rounded-full shadow-xs flex items-center gap-1">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          ใหม่
        </span>
        <span v-else-if="product.subtitle === 'ส่งฟรี'" class="bg-emerald-500 text-white text-[9.5px] font-extrabold px-2.5 py-1 rounded-full shadow-xs flex items-center gap-1">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"></path></svg>
          ส่งฟรี
        </span>
        <span v-else-if="(product.originalPrice || product.original_price) && Number(product.originalPrice || product.original_price) > Number(product.price)" class="bg-red-600 text-white text-[9.5px] font-extrabold px-2.5 py-1 rounded-full shadow-xs flex items-center gap-1">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path></svg>
          ลดแรง
        </span>
      </div>

      <!-- Wishlist Button (Top Right - 20% Compact Size) -->
      <div class="absolute top-3 right-3 z-[4]">
        <button v-if="settingsStore.isWishlistEnabled" @click.prevent="toggleWishlistItem($event, product)" class="w-7 h-7 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 text-gray-400 hover:text-emerald-500 hover:shadow-xs flex items-center justify-center transition-all duration-300 border border-transparent hover:border-gray-100 dark:hover:border-gray-700 backdrop-blur-xs" :title="wishlistStore.isInWishlist(product.id) ? 'นำออกจากรายการโปรด' : 'เพิ่มในรายการโปรด'" :aria-label="wishlistStore.isInWishlist(product.id) ? 'นำออกจากรายการโปรด' : 'เพิ่มในรายการโปรด'">
          <span class="sr-only">{{ wishlistStore.isInWishlist(product.id) ? 'นำออกจากรายการโปรด' : 'เพิ่มในรายการโปรด' }}</span>
          <svg v-if="wishlistStore.isInWishlist(product.id)" class="w-3.5 h-3.5 text-rose-500" fill="currentColor" viewBox="0 0 24 24"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/></svg>
          <svg v-else class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
        </button>
      </div>

      <!-- Out of Stock Watermark -->
      <div v-if="isOutOfStock" class="absolute inset-0 bg-white/50 dark:bg-black/50 backdrop-blur-[1px] flex items-center justify-center z-[2]">
        <span class="text-3xl font-black text-gray-500/30 dark:text-gray-400/30 tracking-[0.2em] -rotate-12 select-none pointer-events-none">หมดแล้ว</span>
      </div>

      <!-- Feature Icons Stack (Left - 20% Compact Size) -->
      <div v-if="cardFeatures && cardFeatures?.enabled !== false && cardFeatures?.show_stack !== false && cardFeatures?.stack?.length > 0" class="absolute top-1/2 -translate-y-1/2 left-2.5 z-[4] flex flex-col gap-2 feat-stack">
        <div v-for="(feat, idx) in cardFeatures.stack" :key="'stack'+idx" class="feat-badge" :style="`--i: ${idx}`">
          <div class="feat-badge-icon">
             <FeatureIcon v-if="feat.icon" :name="feat.icon" class="w-4 h-4" />
          </div>
          <span class="feat-badge-text">{{ feat.text }}</span>
        </div>
      </div>

      <!-- Green Strong Badge (Bottom Right Image - 20% Compact Size) -->
      <div v-if="cardFeatures && cardFeatures?.enabled !== false && cardFeatures?.show_badge !== false && (cardFeatures?.badge?.text1 || cardFeatures?.badge?.text2)" class="absolute bottom-2.5 right-2.5 z-[4] bg-emerald-600 text-white px-2 py-1 rounded-lg shadow-md border border-emerald-500 flex items-center gap-1 transform translate-y-1 group-hover:translate-y-0 transition-transform">
        <FeatureIcon v-if="cardFeatures.badge.icon" :name="cardFeatures.badge.icon" class="w-3.5 h-3.5" />
        <div class="flex flex-col leading-[1.1]">
          <span class="text-[8.5px] font-bold tracking-wide">{{ cardFeatures.badge.text1 }}</span>
          <span v-if="cardFeatures.badge.text2" class="text-[7.5px] font-light">{{ cardFeatures.badge.text2 }}</span>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="p-5 flex flex-col flex-1 bg-white dark:bg-[#121826]">
      <div class="flex items-center justify-between mb-2" v-if="productSku">
        <span class="inline-block text-emerald-700 dark:text-emerald-300 font-extrabold text-[11px] uppercase tracking-wider bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-200/50 dark:border-emerald-800/30">
          SKU: {{ productSku }}
        </span>
      </div>
      <h3 class="text-base md:text-[17px] font-black text-gray-900 dark:text-white leading-snug transition-colors mb-4 flex-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
        {{ product.title || product.name }}
      </h3>
      
      <!-- Pricing & Cart Button (Vibrant Red Selling Price) -->
      <div class="flex items-end justify-between mt-auto pt-2">
        <div class="flex flex-col">
          <div class="flex items-center gap-1.5 mb-0.5" v-if="(product.originalPrice || product.original_price) && Number(product.originalPrice || product.original_price) > Number(product.price)">
            <span class="text-xs text-gray-500 dark:text-gray-400 line-through font-medium">฿{{ Number(product.originalPrice || product.original_price).toLocaleString() }}</span>
            <span class="text-red-700 dark:text-red-400 font-bold text-[11px] bg-red-50 dark:bg-red-900/20 px-1 rounded">-{{ Math.round(((Number(product.originalPrice || product.original_price) - Number(product.price)) / Number(product.originalPrice || product.original_price)) * 100) }}%</span>
          </div>
          <div class="flex items-center gap-1.5 mb-0.5" v-else>
            <span class="text-xs text-gray-400 font-medium opacity-0">฿0</span>
          </div>
          <span class="text-2xl font-black text-red-600 dark:text-red-500 leading-none">฿{{ Number(product.price || 0).toLocaleString() }}</span>
        </div>
        
        <div class="flex items-center gap-2">
          <button v-if="settingsStore.isCompareEnabled && product.compare_enabled !== false && product.compare_enabled !== 0" @click.prevent="toggleCompareItem($event, product)" class="w-10 h-10 rounded-full bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 flex items-center justify-center transition-colors shadow-sm border border-gray-200 dark:border-gray-700" :title="compareStore.isInCompare(product.id) ? 'นำออกจากรายการเปรียบเทียบ' : 'เพิ่มในรายการเปรียบเทียบ'" :aria-label="compareStore.isInCompare(product.id) ? 'นำออกจากรายการเปรียบเทียบ' : 'เพิ่มในรายการเปรียบเทียบ'">
            <span class="sr-only">{{ compareStore.isInCompare(product.id) ? 'นำออกจากรายการเปรียบเทียบ' : 'เพิ่มในรายการเปรียบเทียบ' }}</span>
            <svg class="w-5 h-5 transition-colors" :class="compareStore.isInCompare(product.id) ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-emerald-500'" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path></svg>
          </button>
          <button v-if="settingsStore.isOnlineShoppingEnabled && !isOutOfStock" @click.prevent="addToCart(product)" class="w-11 h-11 rounded-full bg-emerald-600 hover:bg-emerald-500 flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 group/cart" title="เพิ่มลงตะกร้า" aria-label="เพิ่มลงตะกร้า">
             <span class="sr-only">เพิ่มลงตะกร้า</span>
             <svg class="w-5 h-5 text-white transition-transform duration-300 group-hover/cart:-translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
          </button>
          <div v-else-if="!isOutOfStock" class="w-11 h-11 rounded-full bg-gray-50 dark:bg-gray-800 group-hover:bg-emerald-600 border border-gray-100 dark:border-gray-700 dark:group-hover:border-emerald-600 flex items-center justify-center transition-all duration-300 shadow-sm">
             <svg class="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Bottom Feature Bar -->
    <div v-if="cardFeatures && cardFeatures?.enabled !== false && cardFeatures?.show_bottom_bar !== false && cardFeatures?.bottom_bar?.length > 0" class="bg-[#f7faf8] dark:bg-gray-800/60 p-3 px-4 border-t border-gray-100 dark:border-gray-800 grid gap-1 divide-x divide-gray-200 dark:divide-gray-700" :style="`grid-template-columns: repeat(${cardFeatures.bottom_bar.length}, minmax(0, 1fr))`">
      <div v-for="(bar, idx) in cardFeatures.bottom_bar" :key="'bottom'+idx" class="flex items-center justify-center gap-1.5 px-1">
        <FeatureIcon v-if="bar.icon" :name="bar.icon" class="w-4 h-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
        <span class="text-[9px] font-bold text-gray-800 dark:text-gray-200 leading-[1.1]">{{ bar.title }}<br><span v-if="bar.subtitle" class="font-normal text-gray-500 text-[8px]">{{ bar.subtitle }}</span></span>
      </div>
    </div>
  </router-link>
</template>

<style scoped>
/* ── Premium Feature Badge — Single Container ── */
.feat-stack {
  pointer-events: none;
}

.feat-badge {
  display: flex;
  align-items: center;
  gap: 0;
  height: 35px;
  width: 35px;
  min-width: 35px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06),
              0 1px 3px rgba(0, 0, 0, 0.04),
              inset 0 1px 0 rgba(255, 255, 255, 0.8);
  overflow: hidden;
  pointer-events: auto;
  cursor: default;
  opacity: 0;
  transform: translateX(-8px);
  animation: feat-badge-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: calc(var(--i, 0) * 100ms + 200ms);
  transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.3s ease;
}

.group:hover .feat-badge {
  width: auto;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.15),
              0 2px 6px rgba(0, 0, 0, 0.06),
              inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

:deep(.dark) .feat-badge,
.dark .feat-badge {
  background: rgba(18, 24, 38, 0.88);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25),
              0 0 0 1px rgba(16, 185, 129, 0.12),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

@keyframes feat-badge-in {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.feat-badge-icon {
  width: 35px;
  height: 35px;
  min-width: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  flex-shrink: 0;
}

:deep(.dark) .feat-badge-icon,
.dark .feat-badge-icon {
  color: #34d399;
}

.feat-badge-text {
  font-size: 10.5px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  letter-spacing: 0.01em;
  max-width: 0;
  opacity: 0;
  padding-right: 0;
  overflow: hidden;
  transition: max-width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              opacity 0.25s ease,
              padding-right 0.3s ease;
}

.group:hover .feat-badge-text {
  max-width: 140px;
  opacity: 1;
  padding-right: 12px;
}

:deep(.dark) .feat-badge-text,
.dark .feat-badge-text {
  color: #e5e7eb;
}
</style>
